import { useEffect } from "react";
import { toast } from "sonner";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { Header } from "@/components/noxn/Header";
import { Hero } from "@/components/noxn/Hero";
import { TrustStrip } from "@/components/noxn/TrustStrip";
import { WhoItsFor } from "@/components/noxn/WhoItsFor";
import { Services } from "@/components/noxn/Services";
import { UseCases } from "@/components/noxn/UseCases";
import { BeforeAfter } from "@/components/noxn/BeforeAfter";
import { Process } from "@/components/noxn/Process";
import { BudgetGuidance } from "@/components/noxn/BudgetGuidance";
import { PricingPlays } from "@/components/noxn/PricingPlays";
import { ObjectionHandler } from "@/components/noxn/ObjectionHandler";
import { Faq } from "@/components/noxn/Faq";
import { FinalCta } from "@/components/noxn/FinalCta";
import { Footer } from "@/components/noxn/Footer";
import { MobileCtaFloater } from "@/components/noxn/MobileCtaFloater";
import { BackgroundOrbs } from "@/components/noxn/BackgroundOrbs";
import { ScrollProgress } from "@/components/noxn/ScrollProgress";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import AdminScreens from "@/pages/AdminScreens";
import Pipeline from "@/pages/Pipeline";
import PaymentReturn from "@/pages/PaymentReturn";
import Settings from "@/pages/Settings";
import EmailTemplates from "@/pages/EmailTemplates";
import Portal from "@/pages/Portal";
import ContractPortal from "@/pages/ContractPortal";
import ScreenMap from "@/pages/ScreenMap";
import AccountHome from "@/pages/account/AccountHome";
import AccountBook from "@/pages/account/AccountBook";
import AccountRates from "@/pages/account/AccountRates";
import AdminConsole from "@/pages/AdminConsole";
import StaffManagement from "@/pages/StaffManagement";
import Crm from "@/pages/Crm";
import Comms from "@/pages/Comms";
import TasksBoard from "@/pages/TasksBoard";

const ImpersonationBanner = () => {
    const imp = getImpersonation();
    if (!imp?.user) return null;
    return (
        <div data-testid="impersonation-banner"
            className="fixed bottom-0 inset-x-0 z-[998] bg-[#FF5A00] text-white px-4 py-2.5 flex items-center justify-center gap-4 text-xs font-semibold uppercase tracking-widest">
            Viewing as {imp.user.email} — every action is logged
            <button onClick={stopImpersonation} data-testid="impersonation-exit"
                className="border border-white/60 px-3 py-1 hover:bg-white hover:text-[#FF5A00] transition-colors">
                Exit
            </button>
        </div>
    );
};
import AccountReferrals from "@/pages/account/AccountReferrals";
import AccountPlays from "@/pages/account/AccountPlays";
import LandingMinimal from "@/pages/LandingMinimal";
import Book from "@/pages/Book";
import GuestCampaign from "@/pages/GuestCampaign";
import { AuthProvider, useAuth, getImpersonation, stopImpersonation } from "@/lib/auth";
import { ThemeProvider, ThemedApp } from "@/lib/theme";
import { ProtectedRoute } from "@/components/noxn/ProtectedRoute";
import { usePageMeta } from "@/hooks/usePageMeta";

const Landing = () => {
    usePageMeta({
        title: "Noxn Advertising",
        description:
            "NOXN is Australia's digital billboard network. Book premium ad space across Brisbane, Sydney, Melbourne, Perth and Adelaide in minutes. One-time payment, no lock-ins.",
    });
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const shouldResume = searchParams.get("resume") === "1";
    const shouldBegin = searchParams.get("begin") === "1";

    // Referral share link: /?ref=CODE — store for checkout discount
    const refCode = searchParams.get("ref");
    useEffect(() => {
        if (!refCode) return;
        try {
            localStorage.setItem("noxn.referralCode", JSON.stringify({ code: refCode, saved_at: new Date().toISOString() }));
        } catch (e) { /* ignore */ }
        toast.success("Referral link applied — $250 off your first campaign at checkout.");
    }, [refCode]);

    // Account-first flow: the wizard lives inside /account/book.
    const draftToken = searchParams.get("draft");
    useEffect(() => {
        if (draftToken) navigate(`/account/book?draft=${encodeURIComponent(draftToken)}`, { replace: true });
        else if (shouldResume) navigate("/account/book?resume=1", { replace: true });
        else if (shouldBegin) navigate(user ? "/account/book" : "/signup", { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftToken, shouldResume, shouldBegin]);

    // CTAs: logged-out → free account sign-up; logged-in → book (optionally with a screen preselected).
    const onBegin = (screen) => {
        if (!user) { navigate("/signup"); return; }
        navigate(screen?.id ? `/account/book?screen=${screen.id}` : "/account/book");
    };

    return (
        <div className="App font-body relative">
            <ScrollProgress />
            <BackgroundOrbs />
            <Header onBegin={onBegin} />
            <main className="relative z-10 landing-light">
                <Hero onBegin={onBegin} />
                <TrustStrip />
                <WhoItsFor onBegin={onBegin} />
                <Services onBegin={onBegin} />
                <UseCases onBegin={onBegin} />
                <BeforeAfter onBegin={onBegin} />
                <Process />
                <PricingPlays onBegin={onBegin} />
                <ObjectionHandler />
                <Faq />
                <FinalCta onBegin={onBegin} />
            </main>
            <Footer onBegin={onBegin} />
            <MobileCtaFloater onBegin={onBegin} hidden={false} />
        </div>
    );
};

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <ThemeProvider>
                    <Routes>
                        <Route path="/" element={<LandingMinimal />} />
                        <Route path="/classic" element={<Landing />} />
                        <Route path="/v2" element={<LandingMinimal />} />
                        <Route path="/book" element={<Book />} />
                        <Route path="/c/:token" element={<GuestCampaign />} />
                        <Route path="/map" element={<ScreenMap />} />
                        <Route path="/payment/success" element={<PaymentReturn variant="success" />} />
                        <Route path="/payment/cancel" element={<PaymentReturn variant="cancel" />} />
                        <Route path="/portal/contract/:token" element={<ContractPortal />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route
                            path="/account"
                            element={<ProtectedRoute><ThemedApp><AccountHome /></ThemedApp></ProtectedRoute>}
                        />
                        <Route
                            path="/account/book"
                            element={<Navigate to="/book" replace />}
                        />
                        <Route
                            path="/account/rates"
                            element={<ProtectedRoute><ThemedApp><AccountRates /></ThemedApp></ProtectedRoute>}
                        />
                        <Route path="/account/pricing" element={<Navigate to="/account/rates" replace />} />
                        <Route
                            path="/account/plays"
                            element={<ProtectedRoute><ThemedApp><AccountPlays /></ThemedApp></ProtectedRoute>}
                        />
                        <Route
                            path="/account/referrals"
                            element={<ProtectedRoute><ThemedApp><AccountReferrals /></ThemedApp></ProtectedRoute>}
                        />
                        <Route
                            path="/account/settings"
                            element={
                                <ProtectedRoute>
                                    <ThemedApp><Settings /></ThemedApp>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute>
                                    <ThemedApp><Dashboard /></ThemedApp>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/screens"
                            element={
                                <ProtectedRoute roles={["admin", "staff"]}>
                                    <ThemedApp><AdminScreens /></ThemedApp>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/pipeline"
                            element={
                                <ProtectedRoute roles={["admin", "staff"]}>
                                    <ThemedApp><Pipeline /></ThemedApp>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/tasks"
                            element={
                                <ProtectedRoute roles={["admin", "staff"]}>
                                    <ThemedApp><TasksBoard /></ThemedApp>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/comms"
                            element={
                                <ProtectedRoute roles={["admin", "staff"]}>
                                    <ThemedApp><Comms /></ThemedApp>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/crm"
                            element={
                                <ProtectedRoute roles={["admin", "staff"]}>
                                    <ThemedApp><Crm /></ThemedApp>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/staff"
                            element={
                                <ProtectedRoute roles={["admin", "staff"]}>
                                    <ThemedApp><StaffManagement /></ThemedApp>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/admin"
                            element={
                                <ProtectedRoute roles={["admin", "staff"]}>
                                    <ThemedApp><AdminConsole /></ThemedApp>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/settings"
                            element={
                                <ProtectedRoute>
                                    <ThemedApp><Settings /></ThemedApp>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard/settings/emails"
                            element={
                                <ProtectedRoute roles={["admin"]}>
                                    <ThemedApp><EmailTemplates /></ThemedApp>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/portal"
                            element={
                                <ProtectedRoute>
                                    <ThemedApp><Portal /></ThemedApp>
                                </ProtectedRoute>
                            }
                        />
                    </Routes>
                    <ImpersonationBanner />
                    <Toaster position="bottom-right" className="noxn-toaster" />
                </ThemeProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
